import { all, takeLatest, takeEvery } from 'redux-saga/effects'
import Api from '../services/api'

/* ------------- Actions ------------- */
import { AppActions } from '../reducers/AppRedux'
import { AuthActions } from '../reducers/AuthRedux'
import { AssetsActions } from 'src/reducers/AssetsRedux'
import { EntityTypesActions } from 'src/reducers/EntityTypesRedux'
import { EntityClassesActions } from 'src/reducers/EntityClassesRedux'
import { HallsActions } from 'src/reducers/HallsRedux'
import { SitesActions } from 'src/reducers/SitesRedux'
import { RegionsActions } from 'src/reducers/RegionsRedux'

import { CategoriesActions } from '../reducers/CategoriesRedux'
import { NdlDeploymentsActions } from '../reducers/NdlDeploymentsRedux'
import { NdlFlowRunsActions } from '../reducers/NdlFlowRunsRedux'
import { NdlArtifactsActions } from 'src/reducers/NdlArtifactsRedux'

import { RecordingsActions } from 'src/reducers/RecordingsRedux'
import { SmDefinitionsActions } from 'src/reducers/SmDefinitionsRedux'
import { StateDefinitionsActions } from 'src/reducers/StateDefinitionsRedux'
import { StateValuesActions } from 'src/reducers/StateValuesRedux'
import { ContextDefinitionsActions } from 'src/reducers/ContextDefinitionsRedux'
import { ContextValuesActions } from 'src/reducers/ContextValuesRedux'

/* ------------- Sagas ------------- */
import { ready, setLanguage, startup } from './AppSagas'
import { check, login, logout } from './AuthSagas'
import { findAssets, watchAssets, unwatchAssets } from './AssetsSagas'
import { findEntityTypes } from './EntityTypesSagas'
import { findEntityClasses, createEntityClass, updateEntityClass, deleteEntityClass, watchEntityClasses, unwatchEntityClasses } from './EntityClassesSagas'
import { findHalls, createHall, updateHall, deleteHall } from './HallsSagas'
import { findSites } from './SitesSagas'
import { findRegions, createRegion, updateRegion, deleteRegion } from './RegionsSagas'

import { createCategory, findCategories, updateCategory, deleteCategory, getCategory, createdUnit, updatingUnit, watchCategories } from './CategoriesSagas'
import { findNdlDeployments } from './NdlDeploymentsSagas'
import { findNdlFlowRuns, createNdlFlowRun, unwatchFlowRuns, watchFlowRuns } from './NdlFlowRunsSagas'
import { findNdlArtifacts } from './NdlArtifactsSagas'

import { findRecordings, createRecording } from './RecordingsSagas'
import { findSmDefinitions, getSmDefinition, createSmDefinition, updateSmDefinition, deleteSmDefinition, watchSmDefinitions, unwatchSmDefinitions } from "./SmDefinitionsSagas";
import { findStateDefinitions, getStateDefinition, createStateDefinition, updateStateDefinition } from "./StateDefinitionsSagas";
import { findStateValues, getStateValue, watchStateValues, unwatchStateValues } from "./StateValuesSagas";
import { findContextDefinitions, getContextDefinition, watchContextDefinitions, unwatchContextDefinitions } from "./ContextDefinitionsSagas";
import { findContextValues, getContextValue, watchContextValues, unwatchContextValues } from "./ContextValuesSagas";

export default function* root() {
    const api = Api.create()
    yield all([ 
        takeLatest(AppActions.startupRequest, startup),
        takeEvery(AppActions.setLanguage, setLanguage),

        takeLatest(AuthActions.checkRequest, check, api),
        takeLatest(AuthActions.checkFinished, ready),
        takeLatest(AuthActions.loginRequest, login, api),
        takeLatest(AuthActions.logoutRequest, logout, api),

        takeLatest(AssetsActions.findRequest, findAssets, api),
        takeEvery(AssetsActions.watchRequest, watchAssets, api, true),
        takeEvery(AssetsActions.unwatchRequest, unwatchAssets, api, true),

        takeLatest(EntityTypesActions.findRequest, findEntityTypes, api),
        takeLatest(EntityTypesActions.findSuccess, findHalls, api),
        takeLatest(EntityTypesActions.findSuccess, watchAssets, api, true),

        takeLatest(EntityClassesActions.findRequest, findEntityClasses, api),
        takeLatest(EntityClassesActions.createRequest, createEntityClass, api),
        takeLatest(EntityClassesActions.updateRequest, updateEntityClass, api),
        takeLatest(EntityClassesActions.deleteRequest, deleteEntityClass, api),
        takeLatest(EntityClassesActions.watchRequest, watchEntityClasses, api),
        takeLatest(EntityClassesActions.unwatchRequest, unwatchEntityClasses, api),

        takeLatest(HallsActions.createRequest, createHall, api),
        takeLatest(HallsActions.updateRequest, updateHall, api),
        takeLatest(HallsActions.deleteRequest, deleteHall, api),

        takeLatest(SitesActions.findRequest, findSites, api),

        takeEvery(RegionsActions.findRequest, findRegions, api),
        takeLatest(RegionsActions.createRequest, createRegion, api),
        takeLatest(RegionsActions.updateRequest, updateRegion, api),
        takeLatest(RegionsActions.deleteRequest, deleteRegion, api),

        takeEvery(CategoriesActions.findRequest, findCategories, api),
        takeEvery(CategoriesActions.getRequest, getCategory, api),
        takeEvery(CategoriesActions.createRequest, createCategory, api),
        takeEvery(CategoriesActions.updateRequest, updateCategory, api),
        takeEvery(CategoriesActions.deleteRequest, deleteCategory, api),

        takeEvery(NdlDeploymentsActions.findRequest, findNdlDeployments, api),

        takeEvery(NdlFlowRunsActions.findRequest, findNdlFlowRuns, api),
        takeEvery(NdlFlowRunsActions.createRequest, createNdlFlowRun, api),

        takeEvery(NdlArtifactsActions.findRequest, findNdlArtifacts, api),

        takeLatest(NdlFlowRunsActions.watchNdlFlowRunsRequest, watchFlowRuns, api),
        takeLatest(NdlFlowRunsActions.unwatchNdlFlowRunsRequest, unwatchFlowRuns, api),

        takeEvery(RecordingsActions.findRequest, findRecordings, api),
        takeEvery(RecordingsActions.createRequest, createRecording, api),

        takeEvery(SmDefinitionsActions.findRequest, findSmDefinitions, api),
        takeEvery(SmDefinitionsActions.getRequest, getSmDefinition, api),
        takeEvery(SmDefinitionsActions.createRequest, createSmDefinition, api),
        takeEvery(SmDefinitionsActions.updateRequest, updateSmDefinition, api),
        takeEvery(SmDefinitionsActions.deleteRequest, deleteSmDefinition, api),
        takeEvery(SmDefinitionsActions.watchRequest, watchSmDefinitions, api, true),
        takeEvery(SmDefinitionsActions.unwatchRequest, unwatchSmDefinitions, api, true),

        takeEvery(StateDefinitionsActions.findRequest, findStateDefinitions, api),
        takeEvery(StateDefinitionsActions.getRequest, getStateDefinition, api),
        takeEvery(StateDefinitionsActions.createRequest, createStateDefinition, api),
        takeEvery(StateDefinitionsActions.updateRequest, updateStateDefinition, api),

        takeEvery(StateValuesActions.findRequest, findStateValues, api),
        takeEvery(StateValuesActions.getRequest, getStateValue, api),
        takeEvery(StateValuesActions.watchRequest, watchStateValues, api, true),
        takeEvery(StateValuesActions.unwatchRequest, unwatchStateValues, api, true),

        takeEvery(ContextDefinitionsActions.findRequest, findContextDefinitions, api),
        takeEvery(ContextDefinitionsActions.getRequest, getContextDefinition, api),
        takeEvery(ContextDefinitionsActions.watchRequest, watchContextDefinitions, api, true),
        takeEvery(ContextDefinitionsActions.unwatchRequest, unwatchContextDefinitions, api, true),

        takeEvery(ContextValuesActions.findRequest, findContextValues, api),
        takeEvery(ContextValuesActions.getRequest, getContextValue, api),
        takeEvery(ContextValuesActions.watchRequest, watchContextValues, api, true),
        takeEvery(ContextValuesActions.unwatchRequest, unwatchContextValues, api, true),
    ])
}
